import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  DollarSign, 
  RefreshCw, 
  Save, 
  History, 
  TrendingUp,
  AlertCircle,
  Check,
  X,
  Clock
} from 'lucide-react';
import MobileHeader from '../components/MobileHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ExchangeRateSettings = () => {
  const navigate = useNavigate();
  const { user, isGerencia } = useAuth();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedRates, setEditedRates] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!isGerencia()) {
      toast.error('No tienes permiso para acceder a esta sección');
      navigate('/dashboard');
      return;
    }
    fetchRates();
  }, [isGerencia, navigate]);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/exchange-rates`);
      setConfig(response.data);
      // Inicializar tasas editadas con valores actuales
      const initialRates = {};
      Object.entries(response.data.tasas || {}).forEach(([codigo, data]) => {
        initialRates[codigo] = data.tasa_cop;
      });
      setEditedRates(initialRates);
      setHasChanges(false);
    } catch (error) {
      toast.error('Error al cargar las tasas de cambio');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (codigo, value) => {
    const numValue = parseFloat(value) || 0;
    setEditedRates(prev => ({
      ...prev,
      [codigo]: numValue
    }));
    
    // Verificar si hay cambios respecto al original
    const original = config?.tasas?.[codigo]?.tasa_cop || 0;
    const allRatesUnchanged = Object.entries(config?.tasas || {}).every(([code, data]) => {
      if (code === codigo) return numValue === data.tasa_cop;
      return editedRates[code] === data.tasa_cop;
    });
    setHasChanges(!allRatesUnchanged);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Preparar solo las tasas que cambiaron
      const updates = Object.entries(editedRates)
        .filter(([codigo, tasa]) => {
          const original = config?.tasas?.[codigo]?.tasa_cop;
          return tasa !== original && codigo !== 'COP';
        })
        .map(([codigo, tasa_cop]) => ({ codigo, tasa_cop }));
      
      if (updates.length === 0) {
        toast.info('No hay cambios que guardar');
        return;
      }
      
      const response = await axios.put(`${API_URL}/admin/exchange-rates`, updates);
      toast.success('Tasas de cambio actualizadas exitosamente');
      
      // Actualizar config con nuevos valores
      setConfig(prev => ({
        ...prev,
        tasas: response.data.tasas,
        updated_at: new Date().toISOString(),
        updated_by_username: user?.nombre || user?.username
      }));
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar las tasas');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Estás seguro de restaurar las tasas a los valores por defecto?\n\nEsto establecerá:\n• USD: $4,200 COP\n• EUR: $4,500 COP\n• MXN: $202 COP')) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(`${API_URL}/admin/exchange-rates/reset`);
      toast.success('Tasas restauradas a valores por defecto');
      
      // Actualizar estado local
      const initialRates = {};
      Object.entries(response.data.tasas || {}).forEach(([codigo, data]) => {
        initialRates[codigo] = data.tasa_cop;
      });
      setEditedRates(initialRates);
      setConfig(prev => ({
        ...prev,
        tasas: response.data.tasas,
        updated_at: new Date().toISOString()
      }));
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar las tasas');
    } finally {
      setSaving(false);
    }
  };

  const cancelChanges = () => {
    const initialRates = {};
    Object.entries(config?.tasas || {}).forEach(([codigo, data]) => {
      initialRates[codigo] = data.tasa_cop;
    });
    setEditedRates(initialRates);
    setHasChanges(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm uppercase tracking-widest text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  const editableRates = Object.entries(config?.tasas || {}).filter(([codigo]) => codigo !== 'COP');

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header */}
      <header className="bg-black sticky top-0 z-10 hidden md:block">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/60 hover:text-white transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver
              </button>
              <div className="w-px h-6 bg-white/20" />
              <h1 className="font-serif text-lg text-white">Tasas de Cambio</h1>
            </div>
            
            {/* Save/Cancel buttons in header */}
            {hasChanges && (
              <div className="flex items-center gap-2">
                <button
                  onClick={cancelChanges}
                  className="flex items-center gap-2 text-white/60 hover:text-white px-3 py-1 text-xs uppercase tracking-widest transition-colors"
                  data-testid="cancel-button"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-white text-black hover:bg-zinc-200 px-4 py-2 text-xs uppercase tracking-widest transition-all disabled:opacity-50"
                  data-testid="save-button"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-black mb-2">Tasas de Cambio</h1>
          <p className="text-sm text-muted-foreground">
            Administra las tasas de conversión a COP. Estos valores se utilizan para calcular cotizaciones en otras monedas.
          </p>
        </div>

        {/* Alerta de cambios sin guardar */}
        {hasChanges && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-800 font-medium">Hay cambios sin guardar</p>
              <p className="text-xs text-amber-600">Guarda los cambios para que se apliquen en las cotizaciones.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={cancelChanges}
                className="text-xs text-amber-700 hover:text-amber-900 px-3 py-1 border border-amber-300 rounded"
              >
                Descartar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="text-xs bg-amber-600 text-white hover:bg-amber-700 px-3 py-1 rounded disabled:opacity-50"
              >
                Guardar
              </button>
            </div>
          </div>
        )}

        {/* Última actualización */}
        {config?.updated_at && (
          <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Última actualización: {formatDate(config.updated_at)}</span>
            {config.updated_by_username && (
              <span className="text-black">por {config.updated_by_username}</span>
            )}
          </div>
        )}

        {/* Moneda base (COP) */}
        <div className="bg-zinc-100 border border-border/40 shadow-sm p-6 mb-6 rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center font-bold">
              $
            </div>
            <div>
              <h2 className="font-serif text-xl text-black">Peso Colombiano (COP)</h2>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">Moneda Base</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            El COP es la moneda base del sistema. Todas las demás monedas se convierten a COP para cálculos internos.
          </p>
        </div>

        {/* Tasas editables */}
        <div className="bg-white border border-border/40 shadow-sm p-6 mb-6">
          <h2 className="font-serif text-xl text-black mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Tasas de Conversión
          </h2>
          
          <div className="space-y-6">
            {editableRates.map(([codigo, data]) => (
              <div 
                key={codigo}
                className="border-b border-border/40 pb-6 last:border-0 last:pb-0"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Info de la moneda */}
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 bg-zinc-100 rounded-full flex items-center justify-center text-2xl">
                      {data.bandera}
                    </div>
                    <div>
                      <h3 className="font-medium text-black">{data.nombre}</h3>
                      <p className="text-xs text-muted-foreground">{data.simbolo} ({codigo})</p>
                    </div>
                  </div>
                  
                  {/* Input de tasa */}
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground mb-1">1 {codigo} =</p>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-medium">$</span>
                        <input
                          type="number"
                          value={editedRates[codigo] || ''}
                          onChange={(e) => handleRateChange(codigo, e.target.value)}
                          className="w-32 px-3 py-2 text-lg font-medium border border-border rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0"
                          min="0"
                          step="0.01"
                          data-testid={`rate-input-${codigo}`}
                        />
                        <span className="text-lg font-medium text-muted-foreground">COP</span>
                      </div>
                    </div>
                    
                    {/* Indicador de cambio */}
                    {editedRates[codigo] !== data.tasa_cop && (
                      <div className="flex items-center gap-1 text-amber-600">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-xs">Modificado</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Referencia rápida */}
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <span>
                    {data.simbolo}100 = ${formatCurrency((editedRates[codigo] || 0) * 100)} COP
                  </span>
                  <span>
                    {data.simbolo}1,000 = ${formatCurrency((editedRates[codigo] || 0) * 1000)} COP
                  </span>
                  <span>
                    $1,000,000 COP = {data.simbolo}{formatCurrency(1000000 / (editedRates[codigo] || 1))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Acciones */}
        <div className="bg-white border border-border/40 shadow-sm p-6 mb-6">
          <h2 className="font-serif text-xl text-black mb-4">Acciones</h2>
          
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 bg-zinc-100 text-black hover:bg-zinc-200 px-4 py-3 text-xs uppercase tracking-widest transition-all"
              data-testid="toggle-history-button"
            >
              <History className="w-4 h-4" />
              {showHistory ? 'Ocultar Historial' : 'Ver Historial'}
            </button>
            
            <button
              onClick={handleReset}
              disabled={saving}
              className="flex items-center gap-2 bg-white text-black border border-border hover:bg-zinc-50 px-4 py-3 text-xs uppercase tracking-widest transition-all disabled:opacity-50"
              data-testid="reset-button"
            >
              <RefreshCw className="w-4 h-4" />
              Restaurar Valores por Defecto
            </button>
          </div>
        </div>

        {/* Historial de cambios */}
        {showHistory && (
          <div className="bg-white border border-border/40 shadow-sm p-6">
            <h2 className="font-serif text-xl text-black mb-4 flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Cambios
            </h2>
            
            {config?.historial && config.historial.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {[...config.historial].reverse().map((entry, index) => (
                  <div 
                    key={index}
                    className="border-l-2 border-zinc-200 pl-4 py-2"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-black">{entry.usuario}</span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground text-xs">
                        {formatDate(entry.fecha)}
                      </span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {entry.cambios.map((cambio, idx) => (
                        <div key={idx} className="text-xs text-muted-foreground">
                          {cambio.accion ? (
                            <span className="text-amber-600 font-medium">{cambio.accion}</span>
                          ) : (
                            <span>
                              <span className="font-medium">{cambio.moneda}</span>: 
                              ${formatCurrency(cambio.tasa_anterior)} → 
                              <span className="text-black font-medium">${formatCurrency(cambio.tasa_nueva)}</span>
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No hay cambios registrados aún
              </p>
            )}
          </div>
        )}

        {/* Info adicional */}
        <div className="mt-6 bg-zinc-50 border border-border/40 p-6 rounded-lg">
          <h3 className="font-serif text-lg text-black mb-3">¿Cómo funcionan las tasas?</h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              Las tasas se usan para calcular el mínimo de cuotas en otras monedas
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              Los valores en cotizaciones se muestran en la moneda seleccionada
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              Internamente, todo se convierte a COP para cálculos y reportes
            </li>
            <li className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              Actualiza las tasas regularmente según el mercado
            </li>
          </ul>
        </div>

        {/* Mobile save button */}
        {hasChanges && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 md:hidden safe-area-bottom">
            <div className="flex gap-2">
              <button
                onClick={cancelChanges}
                className="flex-1 flex items-center justify-center gap-2 bg-zinc-100 text-black py-3 text-xs uppercase tracking-widest"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-black text-white py-3 text-xs uppercase tracking-widest disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ExchangeRateSettings;
